import { sentenceCase } from "change-case";
import { useState } from "react";
import styled, { RuleSet } from "styled-components";

import { faInfoCircle } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { Status } from "codegen/models/Status";
import { blueStyle, defaultStyle, errorStyle, Filter, greenStyle, yellowStyle } from "pages/Strombestilling/components/Filter";

interface OrderStatusProps {
	status: Status;
	onClick?: () => void;
}
export function OrderStatus({ status, onClick }: Readonly<OrderStatusProps>) {
	const [hovered, setHovered] = useState(false);
	return (
		<Tooltip>
			<OrderFilter as={onClick ? "button" : "div"} onClick={onClick} $status={status}>
				{sentenceCase(status)}
				{onClick && <FontAwesomeIcon icon={faInfoCircle} onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)} />}
			</OrderFilter>
			<TooltipText visible={hovered}>{tooltipMap.get(status)}</TooltipText>
		</Tooltip>
	);
}

const OrderFilter = styled(Filter) <{ $status: Status; onClick: boolean }>`
	${({ $status }) => colorMap.get($status) ?? defaultStyle}
	&:hover {
		opacity: ${(props) => (props.onClick ? 0.7 : 1)};
	}
`;

// Styled Tooltip
export const Tooltip = styled.div`
	display: inline-block;
	position: relative;
`;

export const TooltipText = styled.div<{ visible: boolean }>`
	visibility: ${(props) => (props.visible ? "visible" : "hidden")};
	background-color: #333;
	color: #fff;
	text-align: center;
	border-radius: 4px;
	padding: 16px;
	position: absolute;
	top: 50%;
	left: 100%; // Position to the right of the icon
	transform: translateY(-50%); // Center vertically
	width: 350px;
	white-space: wrap;
	z-index: 1;
	display: ${(props) => (props.visible ? "block" : "none")};
	transition: opacity 0.3s;
	box-shadow: 0px 4px 10px rgba(5, 113, 168, 0.1);
`;

const tooltipMap: Map<Status, string> = new Map([
	[Status.Ny, "Ligger i koden av historiske årsaker. Skal ikke forekomme lenger."],
	[Status.FeiletKredittsjekk, "Avvist kredittsjekk. Forekommer også hvis kunden har aktivert kredittsperre."],
	[
		Status.Bekreftet,
		"Tilstanden settes rett etter bestilling. Før registrering i UC. Status oppdateres deretter når vi får event ContractCreated/ContractUpdated fra UC.",
	],
	[
		Status.AvventerInfo,
		"Tilstanden settes hvis vi ikke har nok informasjon til å registrere avtalen i UC. Brukes for øyeblikket bare dersom kunden ikke har målernummer/mpid.",
	],
	[
		Status.AvventerSamtykke,
		"Tilstanden settes når kunden har startet bestilling, men ikke har signert avtalen enda.",
	],
	[
		Status.SamtykkeFeil,
		"Tilstanden settes når det oppstår en feil under signeringsprosessen.",
	],
	[
		Status.SamtykkeAvvist,
		"Tilstanden settes når kunden avviser signering.",
	],
	[
		Status.SamtykkeUtlopt,
		"Tilstanden settes når kunden ikke signerer samtykke innen fristen har gått ut.",
	],
	[Status.Ferdig, "Avtalen er ferdig registrert i UC (etter UcContractCreated)."],
	[
		Status.ManuellBehandling,
		"Bestillingen er sendt til KS/Backoffice for håndtering. Ble tidligere alltid gjort når brukeren ikke hadde målernummer eller produktet krevde manuell behandling. Nå havner den her bare når produktet krever manuell behandling eller kunden gir opp å registrere målernummer ved AVVENTER_INFO.",
	],
	[Status.Feilet, "Feil ved registrering i UC (uventet 400/500-feil fra UcPostContract). Krever oppfølging!"],
	[
		Status.FeiletRegistrering,
		'Var opprinnelig brukt av RPA ved registrering av ordre i Customer. Etter migrering til UC forekommer denne kun dersom UC sender "Contract Creation Ignored".',
	],
	[
		Status.Aktivert,
		"Hvis vi får aktiveringsmelding fra UC og klarer å knytte den til vår strømbestilling vil status på bestillingen være oppdatert med riktig status. Informativ status som ikke har noen betydning for strømbestillingsforløpet.",
	],
	[
		Status.Annullert,
		"Avtalen er anullert. Forekommer ved bruk av angrerett eller dersom kunden har registrert feil oppstartsdato og fått hjelp av KS. Ved endring av oppstartsdato blir den opprinnelige avtalen kansellert og en ny likelydende avtale opprettet.",
	],
	[Status.KundeVedBestilling, "Eksisterende kunde. Ikke i bruk lenger."],
	[Status.Arkivert, "Avtale arkivert (etter informasjon fra UC)."],
]);

const colorMap: Map<Status, RuleSet<object>> = new Map([
	// grønn
	[Status.Bekreftet, greenStyle],
	[Status.Ferdig, greenStyle],
	[Status.Aktivert, greenStyle],
	// blå
	[Status.ManuellBehandling, blueStyle],
	[Status.AvventerInfo, blueStyle],
	[Status.AvventerSamtykke, blueStyle],
	// gul
	[Status.Annullert, yellowStyle],
	[Status.FeiletRegistrering, yellowStyle],
	// rød
	[Status.Feilet, errorStyle],
	[Status.SamtykkeFeil, errorStyle],
	[Status.SamtykkeAvvist, errorStyle],
	[Status.SamtykkeUtlopt, errorStyle],
	// grå
	[Status.FeiletKredittsjekk, defaultStyle],
	[Status.Arkivert, defaultStyle],
	// grå - ubrukt
	[Status.KundeVedBestilling, defaultStyle],
	[Status.Ny, defaultStyle],
]);
